import React, { useContext, useState } from 'react';
import { Modal, Input, Select, Upload, Button, notification } from 'antd';
import { PlusOutlined, CloseCircleOutlined } from '@ant-design/icons';
import Compressor from 'compressorjs';
import { AppContext } from "../AppContext";
import tr from '../i18n';
const { TextArea } = Input;
const { Option } = Select;

const NewAntdModal = ({ isOpen, onClose, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [workType, setWorkType] = useState('');
  const [workGardenId, setWorkGardenId] = useState(null);
  const [workGardenName, setWorkGardenName] = useState('');
  const [photos, setPhotos] = useState([]); // Array of base64 strings
  const { gardens } = useContext(AppContext);

  // Handle converting file to Base64
  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleModalClose = () => {
    setTitle('');
    setNotes('');
    setWorkType('');
    setWorkGardenId(null);
    setWorkGardenName('');
    setPhotos([]);
  }

  const handlePhotoUpload = async ({ file }) => {
    if (photos.length >= 5) {
      notification.error({
        message: tr('You can upload up to 5 photos only'),
      })
      return;
    }

    new Compressor(file, {
      quality: 0.7, // Adjust quality (0 to 1)
      maxWidth: 800, // Max width for the image
      maxHeight: 800, // Max height for the image
      success: async (compressedFile) => {
        try {
          const base64 = await getBase64(compressedFile);
          setPhotos((prevPhotos) => [...prevPhotos, base64]);
        } catch (error) {
          notification.error({
            message: tr('Failed to upload photo'),
          });
        }
      },
      error: (err) => {
        notification.error({
          message: tr('Image compression failed'),
        });
      },
    });
  };

  const handleRemovePhoto = (index) => {
    setPhotos((prevPhotos) => prevPhotos.filter((_, i) => i !== index));
  };

  const handleGardenChange = (value) => {
    const selectedGarden = gardens.find((garden) => garden.id === value);
    if (selectedGarden) {
      setWorkGardenId(selectedGarden.id);
      setWorkGardenName(selectedGarden.name);
    }
  };

  const validateForm = () => {
    if (!title) {
      notification.error({
        message: tr('Work title is required'),
      });
      return false;
    }

    if (!workGardenId || !workGardenName) {
      notification.error({
        message: tr('Garden is required'),
      });
      return false;
    }

    if (!workType) {
      notification.error({
        message: tr('Work type is required'),
      });
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return; // If validation fails, don't submit
    }

    const data = {
      timeZone: "Asia/Istanbul",
      gardenId: workGardenId,
      gardenName: workGardenName,
      tenantId: "",
      creatorId: "",
      creatorName: "",
      workType: workType,
      workTitle: title,
      assignmentNotes: notes,
      assignmentImages: photos,
      assignedUserIds: []
    };

    onSubmit(data); // Send data to the API
  };

  return (
    <Modal
      title={tr('Create Work Order')}
      width={window.innerWidth < 768 ? "50%" : "40%"}
      visible={isOpen}
      onCancel={() => {
        handleModalClose();
        onClose();
      }}      
      onOk={handleSubmit}
      okText={tr('Save')}
      cancelText={tr('Cancel')}
    >
      <div style={{ marginBottom: '1rem' }}>
        <label>{tr('Work Title')}:</label>
        <Input
          placeholder={tr('Work Title')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <label>{tr('Work Notes')}:</label>
        <TextArea
          placeholder={tr('Work Notes')}
          value={notes}
          rows={4}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <label> {tr('Garden')}:</label>
        <Select
          placeholder={tr('Select Garden')}
          value={workGardenId}
          onChange={handleGardenChange}
          style={{ width: '100%' }}
        >
          {gardens.map((garden, index) => (
            <Option key={garden.id} value={garden.id}>
              {garden.name}
            </Option>
          ))}
        </Select>
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <label> {tr('Work Type')}:</label>
        <Select
          placeholder={tr('Work Type')}
          value={workType}
          onChange={(value) => setWorkType(value)}
          style={{ width: '100%' }}
        >
          <Option value="kontrol">{tr("Control")}</Option>
          <Option value="bakım"> {tr("Maintenance")}</Option>
          <Option value="tamir">{tr("Repair")}</Option>
          <Option value="imalat">{tr("Production")}</Option>
          <Option value="bilgi">{tr("Information")}</Option>
        </Select>
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <label> {tr('Upload Photos (max 5)')} :</label>
        <Upload
          customRequest={handlePhotoUpload}
          listType="picture"
          showUploadList={false}
        >
          {photos.length < 5 && (
            <div className='flex flex-row rounded-md border px-2 py-1 gap-2 items-center ml-3 mb-4'>
              <PlusOutlined /> {tr('Upload')}
            </div>
          )}
        </Upload>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {photos.map((photo, index) => (
            <div key={index} style={styles.photoContainer}>
              <img src={photo} alt={`Preview ${index}`} style={styles.photo} />
              <CloseCircleOutlined
                style={styles.deleteIcon}
                onClick={() => handleRemovePhoto(index)}
              />
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};
const styles = {
  photoContainer: {
    position: 'relative',
    width: '100px',
    height: '120px',
    border: '1px solid #d9d9d9',
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  photo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  deleteIcon: {
    position: 'absolute',
    top: '5px',
    right: '5px',
    fontSize: '16px',
    color: 'red',
    cursor: 'pointer',
  },
};

export default NewAntdModal;